<template>
  <bottom-sheet
    :visible="visible"
    :close-icon-right-align="true"
    :is-full-screen="isMobile"
    @close="$emit('close')"
  >
    <div class="book-now-inputs flex flex-col gap-3 px-3">
      <div class="-mt-4 text-left">
        <h4 class="text-xl text-steel-1100">Book Now</h4>
        <p class="text-steel-700">
          <span class="text-red-500">*</span>
          = Required Fields
        </p>
      </div>

      <div class="flex-between my-2">
        <img
          v-if="brokerLogo"
          class="flex h-8 max-w-48 items-center text-2xs"
          :src="brokerLogo"
          alt="broker logo"
        />

        <div class="flex flex-col items-end gap-2 pt-2">
          <h3 class="text-3xl font-semibold">
            {{ formatPrice(load.price) }}
          </h3>
          <p
            v-if="ratePerMile && load?.price"
            class="-mt-2 text-1xs text-neutral-subtlest"
          >
            {{ ratePerMile }}/mi
          </p>
        </div>
      </div>

      <div class="mb-4 flex flex-col gap-4">
        <p class="text-left text-lg">Booking Info</p>

        <div>
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Ratecon Email <span class="text-red-500">*</span>
          </p>
          <InputComponent
            input-label="ratecon_email"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Rate Con Email"
            :default-input-value="form.ratecon_email"
            :value="form.ratecon_email"
            @input-change="handleInput"
          />
          <p class="mt-1 pl-2 text-left text-sm font-normal text-steel-700">
            Your ratecon will be sent to this email.
          </p>
        </div>

        <p class="text-left text-lg">Dispatching Info</p>

        <div class="booknow-checkbox">
          <CheckboxButton
            item="Driver is same as dispatch"
            :active="driver_same_as_dispatch"
            :handle-select="handleDriverSameAsDispatch"
          />
        </div>

        <div>
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Dispatch Name
          </p>
          <InputComponent
            input-label="dispatch_name"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Name"
            :default-input-value="form.dispatch_name"
            :value="form.dispatch_name"
            @input-change="handleInput"
          />
        </div>

        <div>
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Dispatch Phone
          </p>
          <InputComponent
            input-label="dispatch_phone"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Phone Number"
            :default-input-value="form.dispatch_phone"
            :value="form.dispatch_phone"
            inputType="tel"
            @input-change="handleInput"
          />
        </div>

        <div>
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Dispatch Email
          </p>
          <InputComponent
            input-label="dispatch_email"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Email"
            :default-input-value="form.dispatch_email"
            :value="form.dispatch_email"
            @input-change="handleInput"
          />
        </div>

        <div
          v-if="!driver_same_as_dispatch"
          class="flex flex-col gap-3"
        >
          <div>
            <p class="pl-2 text-left text-sm font-semibold text-steel-800">
              Driver Name
            </p>
            <InputComponent
              input-label="driver_name"
              wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
              placeholder="Enter Name"
              :default-input-value="form.driver_name"
              :value="form.driver_name"
              @input-change="handleInput"
            />
          </div>

          <div>
            <p class="pl-2 text-left text-sm font-semibold text-steel-800">
              Driver Phone
            </p>
            <InputComponent
              input-label="driver_phone"
              wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
              placeholder="Enter Phone"
              :default-input-value="form.driver_phone"
              :value="form.driver_phone"
              @input-change="handleInput"
            />
          </div>
        </div>

        <div class="relative">
          <p class="mb-4 mt-2 text-left text-lg">Vehicle</p>

          <div
            class="relative flex h-10 w-full cursor-pointer items-center rounded-lg border border-steel-300"
            :class="{
              'h-16 bg-steel-100': form.vehicle?.id,
              'h-10 bg-white': !form.vehicle?.id,
            }"
            @click="toggleVehicleDropdown"
          >
            <p
              v-if="!form.vehicle?.id"
              class="pl-2 text-sm text-steel-700"
            >
              Choose Vehicle
            </p>
            <div
              v-else
              class="flex w-full cursor-pointer items-center justify-between px-4 py-2 pr-10"
            >
              <div class="flex items-start gap-2">
                <img
                  class="mt-1 h-5 w-5"
                  src="@/assets/loads/green-wifi.svg"
                  alt="wifi"
                />

                <div class="text-left">
                  <div class="flex justify-between">
                    <div class="text-left">
                      <p class="text-sm text-steel-1100">
                        {{ form?.vehicle?.truck_number || form?.vehicle.id }}
                      </p>
                    </div>
                    <div
                      v-if="form?.vehicle?.license_plate_number"
                      class="ml-2 text-right"
                    >
                      <p class="text-xs text-steel-700">
                        Plate #: {{ form?.vehicle.license_plate_number
                        }}<span v-if="form?.vehicle?.license_plate_state">
                          ({{ form?.vehicle.license_plate_state }})</span
                        >
                      </p>
                    </div>
                  </div>
                  <p class="text-xs text-steel-700">
                    {{ form?.vehicle.vehicle?.vin }}
                  </p>
                </div>
              </div>
            </div>

            <img
              class="absolute right-3 opacity-50"
              src="@/assets/arrow-down-black.svg"
              alt="carrot"
            />
          </div>

          <div
            v-if="showVehicleDropdown"
            class="absolute z-20 flex max-h-[200px] w-full flex-col overflow-scroll rounded-lg border border-steel-300 bg-white py-2"
            :class="{
              'top-[95px]': form.vehicle?.id,
              'top-[70px]': !form.vehicle?.id,
            }"
          >
            <div v-if="filtered_carrier_vehicles?.length > 0">
              <div
                v-for="vehicle in filtered_carrier_vehicles"
                :key="vehicle.id"
                class="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-steel-100"
                @click="handleSelectVehicle(vehicle)"
              >
                <div class="flex items-start gap-2">
                  <img
                    class="mt-1 h-5 w-5"
                    src="@/assets/loads/green-wifi.svg"
                    alt="wifi"
                  />

                  <div class="text-left">
                    <div class="flex justify-between">
                      <div class="text-left">
                        <p class="text-sm text-steel-1100">
                          {{ vehicle?.truck_number || vehicle.id }}
                        </p>
                      </div>
                      <div
                        v-if="vehicle?.license_plate_number"
                        class="ml-2 text-right"
                      >
                        <p class="text-xs text-steel-700">
                          Plate #: {{ vehicle.license_plate_number
                          }}<span v-if="vehicle?.license_plate_state">
                            ({{ vehicle.license_plate_state }})</span
                          >
                        </p>
                      </div>
                    </div>
                    <p class="text-xs text-steel-700">
                      {{ vehicle.vehicle?.vin }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              <p class="text-steel-700">
                There are no current vehicles to select.
              </p>
            </div>
          </div>
        </div>

        <div>
          <p class="pl-2 text-left text-sm font-semibold text-steel-800">
            Trailer Number
          </p>
          <InputComponent
            input-label="trailer_number"
            wrapper-class="flex bg-white text-sm items-center h-10 gap-1.5 px-2"
            placeholder="Enter Trailer Number"
            :default-input-value="form.trailer_number"
            :value="form.trailer_number"
            @input-change="handleInput"
          />
        </div>
      </div>

      <div class="my-4 flex justify-end gap-4">
        <ButtonComponent @click="$emit('close')">Not Now</ButtonComponent>
        <ButtonComponent
          variant="primary"
          class="flex min-w-[110px] justify-center"
          :disabled="isSubmitButtonDisabled"
          @click="handleBookNow"
        >
          <svg
            v-if="isLoading"
            class="loading relative h-5 w-full"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
              stroke="currentColor"
              stroke-width="3"
            />
            <path
              d="M16 1V7L20 4L16 1Z"
              fill="currentColor"
            />
            <path
              d="M22.4895 7.161C24.4444 8.50678 25.9564 10.402 26.8342 12.6071"
              stroke="currentColor"
              stroke-width="3"
            />
          </svg>
          <p v-else>Book Load</p>
        </ButtonComponent>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  onBeforeMount,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useScreenSize } from '@/composables/useScreenSize';
import { isEmail } from '@/common/validators';
import {
  BottomSheet,
  InputComponent,
  ButtonComponent,
  CheckboxButton,
} from '@/components';
import { BookLoadNotification } from '@/components/loads';
import { formatPrice } from '@/utils/currency.js';

const store = useStore();

defineProps({
  visible: Boolean,
});

const emit = defineEmits(['close']);

const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const form = ref({
  ratecon_email: '',
  dispatch_name: '',
  dispatch_phone: '',
  dispatch_email: '',
  trailer_number: '',
  driver_name: '',
  driver_phone: '',
  vehicle: {},
});

const currentUser = computed(() => store.state.users?.currentUser);

onBeforeMount(async () => {
  // eslint-disable-next-line no-unused-vars
  const [_, carrierDetailRes] = await Promise.all([
    store.dispatch('loads/getTruckPostings', {
      query: '?q[is_active_eq]=1',
    }),
    store.dispatch('carriers/getCarriersDetail', {
      id: currentUser?.value?.requested_carrier_id,
    }),
    store.dispatch(
      'loads/getCarrierVehicles',
      currentUser?.value?.requested_carrier_id
    ),
  ]);

  const dispatchConactInfo =
    carrierDetailRes?.data?.contact_information?.dispatch_contact;

  form.value.ratecon_email = dispatchConactInfo?.email_address;
  form.value.dispatch_name = dispatchConactInfo?.name;
  form.value.dispatch_phone = dispatchConactInfo?.phone;
  form.value.dispatch_email = dispatchConactInfo?.email_address;
});

const load = computed(() => store.state.loads.load_details);
const brokerLogo = computed(() => load?.value?.broker_logo?.url);
const ratePerMile = computed(() => {
  return formatPrice(load?.value?.rate_per_mile);
});

const truck_postings = computed(() => store.state.loads?.truck_postings);

const carrier_vehicles = computed(
  () => store.state.loads?.eld_carrier_vehicles
);

const filtered_carrier_vehicles = computed(() => {
  const postedTruckIds = new Set(
    truck_postings.value?.map((truck) => truck.carrier_vehicle?.id)
  );
  return carrier_vehicles.value?.filter(
    (vehicle) => !postedTruckIds.has(vehicle.id)
  );
});

const showVehicleDropdown = ref(false);

const toggleVehicleDropdown = () => {
  showVehicleDropdown.value = !showVehicleDropdown.value;
};

const handleSelectVehicle = (vehicle) => {
  form.value.vehicle = vehicle;
  toggleVehicleDropdown();
};

const driver_same_as_dispatch = ref(false);

const handleDriverSameAsDispatch = () => {
  driver_same_as_dispatch.value = !driver_same_as_dispatch.value;
};

watch(driver_same_as_dispatch, (newValue) => {
  if (newValue) {
    form.value.driver_name = form.value.dispatch_name;
    form.value.driver_phone = form.value.dispatch_phone;
  }
});

const isLoading = ref(false);

const handleInput = (value, name) => {
  form.value[name] = value;

  if (name?.includes('email')) {
    if (isEmail(value)) {
      console.log('email Validated');
    } else {
      console.log('Not VALIDATED');
    }
  }
};

const isSubmitButtonDisabled = computed(() => !form.value.ratecon_email);

const handleBookNow = async () => {
  console.log('Handle book now selection');
  isLoading.value = true;

  setTimeout(async () => {
    // if successful..
    await store.dispatch('toast/addToast', {
      component: BookLoadNotification,
      props: {
        visible: true,
        type: 'success',
      },
    });

    emit('close');
    isLoading.value = false;

    // if error
    // await store.dispatch('toast/addToast', {
    //   component: BookLoadNotification,
    //   props: {
    //     visible: true,
    //     type: 'error',
    //   },
    //   manuallyClose: true,
    // });

    // emit('close');
    // isLoading.value = false;
  }, 500);
};
</script>

<style>
.booknow-checkbox .container {
  @apply pl-9;
}
.booknow-checkbox .container > div {
  @apply font-normal text-steel-900;
}
.booknow-checkbox .container .checkbox-checkmark {
  @apply h-5 w-5;
}
.booknow-checkbox .container .checkbox-checkmark:after {
  border-color: black;
  width: 6px;
  height: 10px;
  border-width: 0 2px 2px 0;
  top: 4px !important;
  left: 7px;
}

.booknow-checkbox .container input:checked ~ .checkbox-checkmark {
  border-color: transparent;
}

.book-now-inputs input {
  @apply text-sm;
}
</style>
